import numeral from 'numeral'

import { BorderStyles, Colors, Services, getServicePathToAPI, joinURLPath, projectName } from '@sigma-frontend-libs/commons'
import Macro from '@sigma-legacy-libs/cache'
import { isUUID } from '@sigmasms/commons'

import { buttonModes, components, getStorageURL, globalErrorHandler, icons, macroCacheOptions, millisecondsToHummerTime, request } from '@/utils'

import { filePlayer, getFileMiscInfo } from '@/components/file/utils'

import render from './render'

const Cache = new Macro(macroCacheOptions)
const cachedGet = Cache.wrapWithCache(async (key, service, id, query) => {
  const pathToAPI = getServicePathToAPI(service)

  return await request.get(joinURLPath(pathToAPI, id), undefined, query)
})

export default {
  name: 'FileSticker',

  mixins: [ filePlayer ],

  props: {
    remove: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      file: undefined,
      loading: {
        file: false,
        audio: false
      },
      localDuration: undefined
    }
  },

  computed: {
    fileMiscInfo() {
      return getFileMiscInfo(this.file)
    },

    sourceDuration() {
      if (this.isCurrentPlayedSource && this.duration) {
        return Math.round(this.duration * 1000)
      }

      return this.localDuration
    },

    sourceProgress() {
      if (!this.isCurrentPlayedSource || !this.duration) {
        return undefined
      }

      const progress = this.currentTime / this.duration * 100

      return Math.min(100, Math.max(0, Number(progress.toFixed(2))))
    },

    label() {
      if (this.fileMiscInfo.type === 'wav') {
        const ms = this.sourceCurrentTime ?? this.localDuration ?? 0

        return millisecondsToHummerTime(ms)
      }

      return numeral(this.file.size).format('0.0b')
    },

    tooltip() {
      switch (this.fileMiscInfo.type) {
        case 'jpg': return [
          {
            name: components.tooltip,
            value: `<img src=${getStorageURL([ this.file?.OwnerId, this.file.id ])} style="max-width: 250px; max-height: 250px">`
          }
        ]
        case 'wav': {
          if (this.playing) {
            return this.getTranslate('misc.buttons.pause')
          }

          return this.getTranslate('misc.buttons.play')
        }
        default: return this.fileMiscInfo.type
      }
    },

    stickerProps() {
      const result = {
        value: this.file?.filename,
        label: this.label,
        loading: this.loading.file,
        disabled: this.disabled,
        borderStyle: BorderStyles.solid,
        progress: this.sourceProgress
      }

      if (this.remove) {
        result.button = {
          mode: buttonModes['flat-icon'],
          icon: icons.clear,
          color: Colors.grey,
          tooltip: this.getTranslate('misc.buttons.remove'),
          callback: () => {
            return this.$emit('remove')
          }
        }
      }

      return result
    }
  },

  watch: {
    value: {
      handler() {
        this.getFile()
      },
      deep: true
    }
  },

  mounted() {
    this.getFile()
  },

  methods: {
    async getFile() {
      if (!this.value) {
        return
      }
      if (isUUID(this.value)) {
        try {
          this.loading.file = true
          const key = [ projectName, Services.storage, 'get', this.value ].join(':')
          const response = await cachedGet(key, Services.storage, this.value, { return: 'meta' })
          if (response) {
            this.file = response
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading.file = false
        }
      } else {
        this.file = this.value
      }

      if (this.fileMiscInfo?.type === 'wav' && this.sourceUrl) {
        this.setDuration(this.sourceUrl)
      }
    },

    setDuration(sourceUrl) {
      const audio = new Audio()

      audio.addEventListener('loadstart', () => {
        this.loading.audio = true
      })
      audio.addEventListener('loadedmetadata', () => {
        this.localDuration = Math.round(audio.duration * 1000)
        this.loading.audio = false
      })

      audio.src = sourceUrl
    },

    toggleAudio() {
      this.setPlayerId(this.localPlayerId)
      this.setSrc(this.sourceUrl)
    }
  },

  render
}
